import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useAuth,
} from "../../auth/AuthContext";

import {
  logAuditEvent,
} from "../../core/audit.service";

import {
  createClient,
  editClient,
  loadClients,
} from "./clientes.service";

import type {
  Client,
  ClientLevel,
} from "./types";

import "./Clientes.css";


const LEVELS:
  ClientLevel[] = [
    "Iniciante",
    "D",
    "C",
    "B",
    "A",
  ];


function getErrorMessage(
  error: unknown,
  fallback: string,
) {
  if (
    typeof error ===
      "object" &&
    error !== null &&
    "message" in error
  ) {
    const message =
      String(
        (
          error as {
            message?: string;
          }
        ).message ??
          "",
      ).trim();


    if (message) {
      return message;
    }
  }


  return fallback;
}


export default function Clientes() {
  const {
    can,
    profile,
    session,
  } =
    useAuth();


  const [
    clients,
    setClients,
  ] =
    useState<Client[]>(
      [],
    );


  const [
    loading,
    setLoading,
  ] =
    useState(
      true,
    );


  const [
    saving,
    setSaving,
  ] =
    useState(
      false,
    );


  const [
    search,
    setSearch,
  ] =
    useState(
      "",
    );


  const [
    error,
    setError,
  ] =
    useState(
      "",
    );


  /*
   * =====================================================
   * CADASTRO
   * =====================================================
   */

  const [
    showForm,
    setShowForm,
  ] =
    useState(
      false,
    );


  const [
    nome,
    setNome,
  ] =
    useState(
      "",
    );


  const [
    telefone,
    setTelefone,
  ] =
    useState(
      "",
    );


  const [
    nivel,
    setNivel,
  ] =
    useState<ClientLevel>(
      "Iniciante",
    );


  /*
   * =====================================================
   * EDIÇÃO
   * =====================================================
   */

  const [
    editingClient,
    setEditingClient,
  ] =
    useState<Client | null>(
      null,
    );


  const [
    editNome,
    setEditNome,
  ] =
    useState(
      "",
    );


  const [
    editTelefone,
    setEditTelefone,
  ] =
    useState(
      "",
    );


  const [
    editNivel,
    setEditNivel,
  ] =
    useState<ClientLevel>(
      "Iniciante",
    );


  /*
   * =====================================================
   * CARREGAMENTO
   * =====================================================
   */

  useEffect(
    () => {
      async function load() {
        try {
          setLoading(
            true,
          );


          const result =
            await loadClients();


          setClients(
            result,
          );


          setError(
            "",
          );
        } catch (err) {
          console.error(
            err,
          );


          setError(
            "Não foi possível carregar os clientes.",
          );
        } finally {
          setLoading(
            false,
          );
        }
      }


      load();
    },
    [],
  );


  /*
   * =====================================================
   * BUSCA
   * =====================================================
   */

  const filteredClients =
    useMemo(
      () => {
        const term =
          search
            .trim()
            .toLowerCase();


        if (!term) {
          return clients;
        }


        return clients.filter(
          (
            client,
          ) =>
            client.nome
              .toLowerCase()
              .includes(
                term,
              ) ||
            (
              client.telefone ||
              ""
            )
              .toLowerCase()
              .includes(
                term,
              ),
        );
      },
      [
        clients,
        search,
      ],
    );


  /*
   * =====================================================
   * CADASTRAR CLIENTE
   * =====================================================
   */

  function openCreate() {
    setError(
      "",
    );

    setNome(
      "",
    );

    setTelefone(
      "",
    );

    setNivel(
      "Iniciante",
    );

    setShowForm(
      true,
    );
  }


  function closeCreate() {
    if (saving) {
      return;
    }


    setShowForm(
      false,
    );

    setError(
      "",
    );
  }


  async function handleCreate() {
    const cleanName =
      nome.trim();


    if (!cleanName) {
      setError(
        "Informe o nome do cliente.",
      );

      return;
    }


    try {
      setSaving(
        true,
      );

      setError(
        "",
      );


      const created =
        await createClient({
          nome:
            cleanName,

          telefone,

          nivel,
        });


      setClients(
        (
          current,
        ) =>
          [
            ...current,
            created,
          ].sort(
            (
              a,
              b,
            ) =>
              a.nome.localeCompare(
                b.nome,
                "pt-BR",
              ),
          ),
      );


      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "CLIENTE_CRIADO",

          level:
            "success",

          module:
            "clientes",

          entityType:
            "cliente",

          entityId:
            created.id,

          description:
            `${profile?.nome || "Funcionário"} cadastrou o cliente ${created.nome}.`,

          metadata: {
            cliente:
              created.nome,

            telefone:
              created.telefone,

            nivel:
              created.nivel,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }


      setShowForm(
        false,
      );

      setNome(
        "",
      );

      setTelefone(
        "",
      );

      setNivel(
        "Iniciante",
      );
    } catch (err) {
      console.error(
        err,
      );


      setError(
        getErrorMessage(
          err,
          "Não foi possível cadastrar o cliente.",
        ),
      );
    } finally {
      setSaving(
        false,
      );
    }
  }


  /*
   * =====================================================
   * EDITAR CLIENTE
   * =====================================================
   */

  function openEdit(
    client: Client,
  ) {
    if (
      !can(
        "clientes.edit",
      )
    ) {
      return;
    }


    setError(
      "",
    );


    setEditingClient(
      client,
    );


    setEditNome(
      client.nome,
    );


    setEditTelefone(
      client.telefone ||
        "",
    );


    setEditNivel(
      (
        client.nivel ||
        "Iniciante"
      ) as ClientLevel,
    );
  }


  function closeEdit() {
    if (saving) {
      return;
    }


    setEditingClient(
      null,
    );

    setError(
      "",
    );
  }


  async function handleEdit() {
    if (
      !editingClient
    ) {
      return;
    }


    if (
      !can(
        "clientes.edit",
      )
    ) {
      setError(
        "Você não possui permissão para editar clientes.",
      );

      return;
    }


    const cleanName =
      editNome.trim();


    if (!cleanName) {
      setError(
        "Informe o nome do cliente.",
      );

      return;
    }


    try {
      setSaving(
        true,
      );

      setError(
        "",
      );


      const updated =
        await editClient({
          id:
            editingClient.id,

          nome:
            cleanName,

          telefone:
            editTelefone,

          nivel:
            editNivel,

          observacao:
            editingClient.observacao,
        });


      setClients(
        (
          current,
        ) =>
          current
            .map(
              (
                client,
              ) =>
                client.id ===
                updated.id
                  ? updated
                  : client,
            )
            .sort(
              (
                a,
                b,
              ) =>
                a.nome.localeCompare(
                  b.nome,
                  "pt-BR",
                ),
            ),
      );


      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "CLIENTE_EDITADO",

          level:
            "info",

          module:
            "clientes",

          entityType:
            "cliente",

          entityId:
            updated.id,

          description:
            `${profile?.nome || "Funcionário"} editou o cliente ${updated.nome}.`,

          metadata: {
            cliente:
              updated.nome,

            telefone:
              updated.telefone,

            nivel:
              updated.nivel,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }


      setEditingClient(
        null,
      );
    } catch (err) {
      console.error(
        err,
      );


      setError(
        getErrorMessage(
          err,
          "Não foi possível editar o cliente.",
        ),
      );
    } finally {
      setSaving(
        false,
      );
    }
  }


  /*
   * =====================================================
   * ACESSO
   * =====================================================
   */

  if (
    !can(
      "clientes.view",
    )
  ) {
    return (
      <section className="clients-page">
        <div className="clients-empty">
          Você não possui acesso aos clientes.
        </div>
      </section>
    );
  }


  return (
    <section className="clients-page">
      <div className="clients-header">
        <div>
          <span className="clients-eyebrow">
            RELACIONAMENTO
          </span>


          <h2>
            Clientes
          </h2>


          <p>
            Cadastre e encontre rapidamente quem frequenta a Arena.
          </p>
        </div>


        {can(
          "clientes.create",
        ) && (
          <button
            type="button"
            className="new-client-button"
            onClick={
              openCreate
            }
          >
            + Novo cliente
          </button>
        )}
      </div>


      <div className="clients-toolbar">
        <input
          type="search"
          placeholder="Buscar por nome ou telefone..."
          value={
            search
          }
          onChange={(
            event,
          ) =>
            setSearch(
              event.target
                .value,
            )
          }
        />


        <span>
          {
            filteredClients.length
          }{" "}
          {
            filteredClients.length ===
            1
              ? "cliente"
              : "clientes"
          }
        </span>
      </div>


      {error &&
        !showForm &&
        !editingClient && (
          <div className="clients-error">
            {error}
          </div>
        )}


      {loading ? (
        <div className="clients-empty">
          Carregando clientes...
        </div>
      ) : filteredClients.length ===
        0 ? (
        <div className="clients-empty">
          Nenhum cliente encontrado.
        </div>
      ) : (
        <div className="clients-grid">
          {filteredClients.map(
            (
              client,
            ) => (
              <article
                key={
                  client.id
                }
                className="client-card"
              >
                <div className="client-avatar">
                  {client.nome
                    .charAt(
                      0,
                    )
                    .toUpperCase()}
                </div>


                <div className="client-info">
                  <strong>
                    {
                      client.nome
                    }
                  </strong>


                  <span>
                    {client.telefone ||
                      "Sem telefone"}
                  </span>
                </div>


                <span className="client-level">
                  {client.nivel ||
                    "Sem nível"}
                </span>


                {can(
                  "clientes.edit",
                ) && (
                  <button
                    type="button"
                    className="client-edit-button"
                    onClick={() =>
                      openEdit(
                        client,
                      )
                    }
                  >
                    Editar
                  </button>
                )}
              </article>
            ),
          )}
        </div>
      )}


      {/* =================================================
          NOVO CLIENTE
      ================================================= */}

      {showForm && (
        <div
          className="client-modal-backdrop"
          onClick={
            closeCreate
          }
        >
          <div
            className="client-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="client-modal-header">
              <div>
                <span className="clients-eyebrow">
                  CADASTRO
                </span>


                <h3>
                  Novo cliente
                </h3>
              </div>


              <button
                type="button"
                onClick={
                  closeCreate
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>


            <label>
              Nome

              <input
                autoFocus
                value={
                  nome
                }
                onChange={(
                  event,
                ) =>
                  setNome(
                    event.target
                      .value,
                  )
                }
                placeholder="Nome do cliente"
              />
            </label>


            <label>
              Telefone

              <input
                value={
                  telefone
                }
                onChange={(
                  event,
                ) =>
                  setTelefone(
                    event.target
                      .value,
                  )
                }
                placeholder="(99) 99999-9999"
              />
            </label>


            <label>
              Nível

              <select
                value={
                  nivel
                }
                onChange={(
                  event,
                ) =>
                  setNivel(
                    event.target
                      .value as ClientLevel,
                  )
                }
              >
                {LEVELS.map(
                  (
                    level,
                  ) => (
                    <option
                      key={
                        level
                      }
                      value={
                        level
                      }
                    >
                      {
                        level
                      }
                    </option>
                  ),
                )}
              </select>
            </label>


            {error && (
              <div className="clients-error">
                {error}
              </div>
            )}


            <div className="client-modal-actions">
              <button
                type="button"
                className="client-cancel"
                onClick={
                  closeCreate
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>


              <button
                type="button"
                className="client-save"
                onClick={
                  handleCreate
                }
                disabled={
                  saving ||
                  !nome.trim()
                }
              >
                {saving
                  ? "Salvando..."
                  : "Cadastrar cliente"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* =================================================
          EDITAR CLIENTE
      ================================================= */}

      {editingClient && (
        <div
          className="client-modal-backdrop"
          onClick={
            closeEdit
          }
        >
          <div
            className="client-modal"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
          >
            <div className="client-modal-header">
              <div>
                <span className="clients-eyebrow">
                  EDIÇÃO
                </span>


                <h3>
                  Editar cliente
                </h3>
              </div>


              <button
                type="button"
                onClick={
                  closeEdit
                }
                disabled={
                  saving
                }
              >
                ×
              </button>
            </div>


            <label>
              Nome

              <input
                autoFocus
                value={
                  editNome
                }
                onChange={(
                  event,
                ) =>
                  setEditNome(
                    event.target
                      .value,
                  )
                }
                placeholder="Nome do cliente"
              />
            </label>


            <label>
              Telefone

              <input
                value={
                  editTelefone
                }
                onChange={(
                  event,
                ) =>
                  setEditTelefone(
                    event.target
                      .value,
                  )
                }
                placeholder="(99) 99999-9999"
              />
            </label>


            <label>
              Nível

              <select
                value={
                  editNivel
                }
                onChange={(
                  event,
                ) =>
                  setEditNivel(
                    event.target
                      .value as ClientLevel,
                  )
                }
              >
                {LEVELS.map(
                  (
                    level,
                  ) => (
                    <option
                      key={
                        level
                      }
                      value={
                        level
                      }
                    >
                      {
                        level
                      }
                    </option>
                  ),
                )}
              </select>
            </label>


            {error && (
              <div className="clients-error">
                {error}
              </div>
            )}


            <div className="client-modal-actions">
              <button
                type="button"
                className="client-cancel"
                onClick={
                  closeEdit
                }
                disabled={
                  saving
                }
              >
                Cancelar
              </button>


              <button
                type="button"
                className="client-save"
                onClick={
                  handleEdit
                }
                disabled={
                  saving ||
                  !editNome.trim()
                }
              >
                {saving
                  ? "Salvando..."
                  : "Salvar alterações"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}